import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { RFQ_STATUSES, StatusBadge } from "./RFQs";
import { useAuth } from "./AuthContext";

const OPEN_STATUSES = ["NEW", "REVIEW", "SOURCING", "SUPPLIER_QUOTES_PENDING", "ANALYSIS", "CUSTOMER_QUOTE", "NEGOTIATION"];
const PRIORITY_RANK = { URGENT: 0, HIGH: 1, NORMAL: 2, LOW: 3 };
const PRIORITY_CLS = {
  URGENT: "bg-red-100 text-red-700", HIGH: "bg-amber-100 text-amber-700",
  NORMAL: "bg-slate-100 text-slate-500", LOW: "bg-slate-100 text-slate-400",
};

const ageDays = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? 0 : Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const QUEUE_LABELS = [
  ["new_rfqs", "New RFQs"], ["review", "Needs Review"], ["sourcing", "Sourcing"],
  ["supplier_quotes_pending", "Supplier Quotes Pending"], ["customer_quotes_pending", "Customer Quotes Pending"],
  ["followups", "Follow-Ups"], ["expiring_quotes", "Expiring Quotes"],
  ["orders_fulfillment", "Orders In Fulfillment"], ["overdue_tasks", "Overdue Tasks"],
];
const QUEUE_LINKS = { followups: "/admin/followups", expiring_quotes: "/admin/followups", orders_fulfillment: "/admin/orders", new_rfqs: "/admin/rfqs", review: "/admin/rfqs", sourcing: "/admin/rfqs", customer_quotes_pending: "/admin/rfqs" };

function Workqueues() {
  const [q, setQ] = useState(null);
  useEffect(() => { api.get("/admin/workqueues").then((r) => setQ(r.data)).catch(() => setQ({})); }, []);
  if (!q) return null;
  return (
    <div className="mt-5 flex flex-wrap gap-2.5" data-testid="workqueue-strip">
      {QUEUE_LABELS.map(([key, label]) => {
        const inner = (
          <>
            <span className={`font-[Poppins] font-extrabold text-[17px] ${key === "overdue_tasks" && q[key] > 0 ? "text-red-600" : "text-[var(--pg-navy)]"}`}>{q[key] ?? 0}</span>
            <span className="text-[11.5px] text-slate-500 font-[Poppins] font-semibold">{label}</span>
          </>
        );
        const cls = "flex flex-col items-start bg-white border border-slate-200 rounded-xl px-4 py-2.5 hover:border-[var(--pg-accent)]";
        return QUEUE_LINKS[key]
          ? <Link key={key} to={QUEUE_LINKS[key]} data-testid={`queue-chip-${key}`} className={cls}>{inner}</Link>
          : <div key={key} data-testid={`queue-chip-${key}`} className={cls}>{inner}</div>;
      })}
    </div>
  );
}

function Tasks({ user }) {
  const [tasks, setTasks] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ title: "", assigned_to: user?.email || "", priority: "NORMAL", due_date: "", next_action: "" });
  const load = useCallback(() => { api.get("/admin/tasks").then((r) => setTasks(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.title.trim()) { toast.error("Task title required"); return; }
    try { await api.post("/admin/tasks", f); toast.success("Task created"); setShow(false); setF((x) => ({ ...x, title: "", due_date: "", next_action: "" })); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const done = async (t) => {
    try { await api.patch(`/admin/tasks/${t.id}`, { status: "DONE" }); toast.success("Task done"); load(); }
    catch { toast.error("Failed"); }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="mt-8" data-testid="tasks-section">
      <div className="flex items-center justify-between">
        <h2 className="font-[Poppins] font-bold text-[18px] text-[var(--pg-navy)]">Open Tasks ({tasks.length})</h2>
        <button data-testid="task-add-toggle" onClick={() => setShow(!show)} className="btn-primary !py-2 !px-3.5 text-[12.5px]">+ New Task</button>
      </div>
      {show && (
        <div className="mt-3 bg-white border border-slate-200 rounded-xl p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="task-form">
          <input data-testid="task-title-input" className="pg-input !py-2.5 lg:col-span-2" placeholder="Task title *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <input data-testid="task-assignee-input" className="pg-input !py-2.5" placeholder="Assignee email" value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} />
          <select data-testid="task-priority-select" className="pg-input !py-2.5" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
            {["LOW", "NORMAL", "HIGH", "URGENT"].map((p) => <option key={p}>{p}</option>)}
          </select>
          <input data-testid="task-due-input" type="date" className="pg-input !py-2.5" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
          <input data-testid="task-next-action-input" className="pg-input !py-2.5 lg:col-span-4" placeholder="Next action" value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} />
          <button data-testid="task-save-button" onClick={add} className="btn-primary !py-2.5 text-[13px]">Create Task</button>
        </div>
      )}
      <div className="mt-3 space-y-2">
        {tasks.length === 0 && <p className="text-[13px] text-slate-400" data-testid="tasks-empty">No open tasks.</p>}
        {tasks.map((t) => (
          <div key={t.id} data-testid={`task-row-${t.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-[13px]">
            <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-bold ${PRIORITY_CLS[t.priority] || "bg-slate-100 text-slate-500"}`}>{t.priority}</span>
            <span className="font-medium text-[var(--pg-navy)]">{t.title}</span>
            {t.next_action && <span className="text-slate-500">→ {t.next_action}</span>}
            {t.assigned_to && <span className="text-slate-400 text-[12px]">{t.assigned_to}</span>}
            {t.due_date && <span className={`text-[12px] font-medium ${t.due_date < today ? "text-red-600" : "text-slate-500"}`}>Due {t.due_date}{t.due_date < today ? " (overdue)" : ""}</span>}
            <button data-testid={`task-done-${t.id}`} onClick={() => done(t)} className="ml-auto text-[12px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200">Done</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MyQueue() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    if (!user?.email) return;
    api.get("/admin/rfqs", { params: { assigned: user.email } })
      .then((r) => {
        const open = r.data.filter((x) => OPEN_STATUSES.includes(x.status));
        open.sort((a, b) => (PRIORITY_RANK[a.priority || "NORMAL"] - PRIORITY_RANK[b.priority || "NORMAL"]) || (a.created_at || "").localeCompare(b.created_at || ""));
        setItems(open);
      })
      .catch(() => setItems([]));
  }, [user?.email]);
  useEffect(() => { load(); }, [load]);

  const advance = async (rfq, status) => {
    try {
      await api.patch(`/admin/rfqs/${rfq.id}`, { status });
      toast.success(`${rfq.rfq_number} → ${status.replace(/_/g, " ")}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  if (!items) return <div className="text-slate-500 p-8" data-testid="queue-loading">Loading your queue...</div>;

  return (
    <div data-testid="admin-my-queue">
      <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">My Queue</h1>
      <p className="mt-1 text-[13.5px] text-slate-500">Open RFQs assigned to you, most urgent first.</p>
      <Workqueues />

      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13.5px] min-w-[820px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-5 py-3">Priority</th><th className="px-5 py-3">RFQ #</th><th className="px-5 py-3">Company</th>
              <th className="px-5 py-3">Product</th><th className="px-5 py-3">Age</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Advance</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400" data-testid="queue-empty">
                Your queue is clear — no open RFQs assigned to you.
              </td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`queue-row-${r.rfq_number}`}>
                <td className="px-5 py-3.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-bold ${PRIORITY_CLS[r.priority || "NORMAL"]}`}>{r.priority || "NORMAL"}</span>
                </td>
                <td className="px-5 py-3.5">
                  <Link to={`/admin/rfqs/${r.id}`} className="font-[Poppins] font-semibold text-[var(--pg-accent)] hover:underline">{r.rfq_number}</Link>
                </td>
                <td className="px-5 py-3.5 font-medium text-[var(--pg-navy)]">{r.company || "—"}</td>
                <td className="px-5 py-3.5 max-w-[220px] truncate">{r.product_service}</td>
                <td className={`px-5 py-3.5 whitespace-nowrap ${ageDays(r.created_at) >= 7 ? "text-red-600 font-semibold" : "text-slate-500"}`}>{ageDays(r.created_at)}d</td>
                <td className="px-5 py-3.5"><StatusBadge status={r.status} /></td>
                <td className="px-5 py-3.5">
                  <select data-testid={`queue-status-${r.rfq_number}`} className="pg-input !py-1.5 !px-2 !text-[12px] w-[190px]"
                    value={r.status} onChange={(e) => advance(r, e.target.value)}>
                    {RFQ_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Tasks user={user} />
    </div>
  );
}
